// The atomizer: turns a Source's normalized text into News Atom v1.0 records.
//
// Best practices baked in from the news-atom research:
//  - EXTRACT, NEVER INVENT: `statement`/`primary_expression` are taken from the source.
//  - PROVENANCE-FIRST: every atom carries `media_anchor` (source + in-clip timecode).
//  - CORROBORATION: identical atoms across sources collapse into one with many anchors.
//  - KNOWLEDGE FRAMES: `knowledge_frame` per Allan Bell (action/reaction/…).
//  - HUMAN REVIEW: emitted with review_process.status = 'candidate'.
//
// The `Atomizer` interface is the swap seam: `heuristicAtomizer` runs client-side;
// `llmAtomizer` (Gemini via Vertex, backend) drops in behind it.

import type { AtomType, EntityKind, EntityRef, KnowledgeFrame, MediaAnchor, NewsAtom, Source } from './types';

export const NEWS_ATOM_VERSION = 'news-atom/1.0';

export interface Atomizer {
  key: string;
  label: string;
  description: string;
  available: boolean;
  atomizeSource: (source: Source) => NewsAtom[];
}

// ---------------------------------------------------------------------------
// Normalization — collapse the shot-log dialect to clean, readable text.
// ---------------------------------------------------------------------------

const SHOT_PREFIX = /^\s*shot\s+[\d/.\- ]+#*/i;

export function normalizeAbstract(raw: string): string {
  if (!raw) return '';
  let t = raw.replace(/�/g, "'");
  if (/file uploaded by user/i.test(t) && /intervention:/i.test(t)) return '';
  t = t.replace(/suggest archive/gi, ' ');
  t = t.replace(SHOT_PREFIX, ' ');
  t = t.replace(/##/g, ' . ').replace(/\/\//g, ' . ');
  t = t.replace(/\s*\.(?:\s*\.)+/g, '. ');
  t = t.replace(/\s+/g, ' ').trim();
  t = t.replace(/^[.\s]+/, '').trim();
  return t;
}

function segments(text: string): string[] {
  return text
    .split(/(?:\.\s+|;\s+)/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// ---------------------------------------------------------------------------
// Lightweight domain gazetteer (stand-in for the NER a real system would do).
// ---------------------------------------------------------------------------

const GAZETTEER: { name: string; kind: EntityKind }[] = [
  { name: 'Kobe Bryant', kind: 'person' },
  { name: 'Gianna Bryant', kind: 'person' },
  { name: 'Kurt Deetz', kind: 'person' },
  { name: 'Peter Goelz', kind: 'person' },
  { name: 'Scott Daehlin', kind: 'person' },
  { name: 'Danny Trejo', kind: 'person' },
  { name: 'Paul Vercammen', kind: 'person' },
  { name: 'Dan Luchy', kind: 'person' },
  { name: 'Matt Mauser', kind: 'person' },
  { name: 'Christina Mauser', kind: 'person' },
  { name: 'Matthew Patterson', kind: 'person' },
  { name: 'John Altobelli', kind: 'person' },
  { name: 'Tony Altobelli', kind: 'person' },
  { name: 'Nate Johnson', kind: 'person' },
  { name: 'Juan Gutierrez', kind: 'person' },
  { name: 'Jason Kehler', kind: 'person' },
  { name: 'Kent Babb', kind: 'person' },
  { name: 'Brian Todd', kind: 'person' },
  { name: 'David Zirin', kind: 'person' },
  { name: "Miles O'Brien", kind: 'person' },
  { name: 'Caron Butler', kind: 'person' },
  { name: 'Massimo Ambrosini', kind: 'person' },
  { name: 'Paolo Boiardi', kind: 'person' },
  { name: 'NTSB', kind: 'org' },
  { name: 'CNN', kind: 'org' },
  { name: 'Orange Coast College', kind: 'org' },
  { name: 'AC Milan', kind: 'org' },
  { name: 'Los Angeles Lakers', kind: 'org' },
  { name: 'Mamba Sports Academy', kind: 'org' },
  { name: 'Staples Center', kind: 'place' },
  { name: 'Calabasas', kind: 'place' },
  { name: 'Our Lady Queen of Angels', kind: 'place' },
  { name: 'Empire State Building', kind: 'place' },
  { name: 'Madison Square Garden', kind: 'place' },
  { name: 'Italy', kind: 'place' },
];

function findEntities(text: string): EntityRef[] {
  const found = new Map<string, EntityRef>();
  for (const e of GAZETTEER) {
    const re = new RegExp(`\\b${e.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (re.test(text)) found.set(e.name.toLowerCase(), e);
  }
  const roleRe = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2}),\s+[a-z]/g;
  let m: RegExpExecArray | null;
  while ((m = roleRe.exec(text)) !== null) {
    const name = m[1].trim();
    if (!found.has(name.toLowerCase())) found.set(name.toLowerCase(), { name, kind: 'person' });
  }
  return [...found.values()];
}

function firstOfKind(entities: EntityRef[], kind: EntityKind): string | undefined {
  return entities.find((e) => e.kind === kind)?.name;
}

function timecodes(text: string): string[] {
  const out: string[] = [];
  const re = /@\s?(\d{1,2}:\d{2})/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) out.push(m[1]);
  return out;
}

function frameForCategory(category?: string): KnowledgeFrame {
  const c = (category ?? '').toLowerCase();
  if (c.includes('crash') || c.includes('investigation')) return 'action';
  if (c.includes('ntsb') || c.includes('expert')) return 'consequence';
  if (c.includes('archive') || c.includes('italy')) return 'history';
  return 'reaction'; // memorials, vigils, fan reactions, tributes, interviews
}

let counter = 0;
function makeAtom(
  source: Source,
  atomType: AtomType,
  statement: string,
  opts: { entities?: EntityRef[]; timecode?: string; timecodeEnd?: string; confidence: number },
): NewsAtom {
  counter += 1;
  const entities = opts.entities ?? [];
  return {
    atom_id: crypto.randomUUID(),
    version: NEWS_ATOM_VERSION,
    atom_status: 'active',
    knowledge_frame: frameForCategory(source.category),
    statement,
    semantic_frame: {
      actor: firstOfKind(entities, 'person') ?? firstOfKind(entities, 'org'),
      location: firstOfKind(entities, 'place'),
    },
    primary_expression: statement,
    media_anchor: [
      {
        source_id: source.id,
        external_id: source.externalId,
        source_title: source.title,
        media_type: source.kind,
        timecode: opts.timecode,
        timecode_end: opts.timecodeEnd,
      },
    ],
    origin: { producer: 'heuristic', created_at: Date.now() + counter, tool_version: '0.1' },
    review_process: { status: 'candidate', method: 'heuristic', confidence: opts.confidence },
    license: { terms: 'internal-only' },
    event_frame: { event_label: source.category },
    topic_id: source.topicId,
    language: 'en',
    atom_type: atomType,
    entities,
    tags: source.category ? [source.category] : [],
  };
}

const QUOTE_RE = /\b(ISO|interview with|guest:|pretape|SOTS?|reax|sound of|remembers)\b/i;
const FACT_RE = /\b(confirmed|said|added|voted|according to|will play|will be held|announced|reported)\b/i;
const STAT_RE = new RegExp(
  '(\\$\\d[\\d,.]*)|' +
    '(\\b\\d[\\d,.]*\\s?(%|percent|million|billion|thousand|dollars?|people|victims?|deaths?|years?)\\b)|' +
    '(\\b\\d{1,3}(st|nd|rd|th)\\b)|(\\b\\d{1,2}[ap]m?ET\\b)',
  'i',
);
const CONTEXT_RE =
  /\b(previously|formerly|former|since \d|background|history|childhood|born|founded|in \d{4}|years ago|archive)\b/i;
const MEDIA_RE =
  /\b(broll|b-roll|aerials?|time ?lapse|lights?|mural|exteriors?|wreckage|debris|crowds?|flowers?|balloons?|memorial|vigil|jerseys?)\b/i;

function atomizeHeuristic(source: Source): NewsAtom[] {
  const atoms: NewsAtom[] = [];
  const text = source.normalizedText;

  // The raw clip is itself a media atom (a reusable A/V asset) — full in/out range.
  atoms.push(
    makeAtom(source, 'media', `Raw footage — ${source.title}`, {
      timecodeEnd: source.duration,
      confidence: 0.9,
      entities: findEntities(source.title),
    }),
  );

  if (!text) return atoms;

  for (const ent of findEntities(text)) {
    atoms.push(makeAtom(source, 'entity', ent.name, { entities: [ent], confidence: 0.7 }));
  }

  let extracted = 0;
  for (const seg of segments(text)) {
    if (extracted >= 5) break;
    if (seg.length < 12) continue;
    const tc = timecodes(seg)[0];
    const ents = findEntities(seg);

    let atomType: AtomType;
    let confidence: number;
    if (QUOTE_RE.test(seg)) {
      atomType = 'quote';
      confidence = 0.75;
    } else if (STAT_RE.test(seg)) {
      atomType = 'statistic';
      confidence = 0.6;
    } else if (FACT_RE.test(seg)) {
      atomType = 'fact';
      confidence = 0.65;
    } else if (CONTEXT_RE.test(seg)) {
      atomType = 'context';
      confidence = 0.55;
    } else if (MEDIA_RE.test(seg) || tc) {
      atomType = 'media';
      confidence = 0.6;
    } else {
      atomType = 'event';
      confidence = 0.55;
    }

    atoms.push(makeAtom(source, atomType, seg, { entities: ents, timecode: tc, confidence }));
    extracted += 1;
  }

  return atoms;
}

export const heuristicAtomizer: Atomizer = {
  key: 'heuristic',
  label: 'Rules-based (runs here, no setup)',
  description: 'Extracts atoms with pattern rules over the source text. Fast and free — good for a first pass.',
  available: true,
  atomizeSource: atomizeHeuristic,
};

export const llmAtomizer: Atomizer = {
  key: 'llm',
  label: 'AI extraction (Gemini via Vertex)',
  description: 'Grounded LLM extraction on the backend — richer claims, quotes and entities. Requires the deployed backend.',
  available: false, // runs server-side; the UI enables it in cloud mode
  atomizeSource: () => {
    throw new Error('LLM atomizer runs server-side (call the backend /atomize endpoint)');
  },
};

export const ATOMIZERS: Atomizer[] = [heuristicAtomizer, llmAtomizer];

// ---------------------------------------------------------------------------
// Cross-source clustering: collapse duplicates into corroborated atoms.
// ---------------------------------------------------------------------------

function clusterKey(a: NewsAtom): string {
  if (a.atom_type === 'entity') {
    return `entity:${a.entities[0]?.name.toLowerCase() ?? a.statement.toLowerCase()}`;
  }
  const norm = a.statement
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return `${a.atom_type}:${norm.slice(0, 90)}`;
}

export function clusterAtoms(atoms: NewsAtom[]): NewsAtom[] {
  const byKey = new Map<string, NewsAtom>();
  for (const a of atoms) {
    const key = clusterKey(a);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...a, media_anchor: [...a.media_anchor], entities: [...a.entities], tags: [...a.tags] });
      continue;
    }
    for (const p of a.media_anchor) {
      if (!existing.media_anchor.some((q) => q.source_id === p.source_id && q.timecode === p.timecode)) {
        existing.media_anchor.push(p);
      }
    }
    for (const e of a.entities) {
      if (!existing.entities.some((x) => x.name.toLowerCase() === e.name.toLowerCase())) {
        existing.entities.push(e);
      }
    }
    for (const t of a.tags) if (!existing.tags.includes(t)) existing.tags.push(t);
    existing.review_process.confidence = Math.min(
      0.98,
      Math.max(existing.review_process.confidence, a.review_process.confidence) + 0.05,
    );
  }
  return [...byKey.values()].sort((a, b) => b.media_anchor.length - a.media_anchor.length);
}

export function runAtomization(sources: Source[], atomizer: Atomizer): NewsAtom[] {
  const raw = sources.flatMap((s) => atomizer.atomizeSource(s));
  return clusterAtoms(raw);
}

// Attach a media_anchor helper type re-export for consumers that map anchors.
export type { MediaAnchor };
